import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Search } from "lucide-react";

/**
 * Diálogo reutilizable para compartir Tarea/Evento por correo (PDF).
 *
 * Props:
 *   open, onOpenChange
 *   tipo: "tarea" | "evento"
 *   item: el registro (con id y titulo)
 *   endpoint: ruta API (ej. "/tareas/{id}/enviar-pdf" o "/calendar/eventos/{id}/enviar-pdf")
 */
export default function ShareByEmailDialog({ open, onOpenChange, tipo, item, endpoint }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mensaje, setMensaje] = useState("");
  const [filter, setFilter] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setMensaje("");
    setFilter("");
    api.get("/users/basic")
      .then(({ data }) => setUsers((data || []).filter(u => u.activo && u.email)))
      .catch((err) => { console.warn("load users:", err); toast.error("No se pudo cargar usuarios"); });
  }, [open]);

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const filtered = users.filter(u => {
    if (!filter.trim()) return true;
    const txt = `${u.nombres || ""} ${u.apellidos || ""} ${u.email || ""}`.toLowerCase();
    return txt.includes(filter.toLowerCase());
  });

  const send = async () => {
    if (selected.size === 0) {
      toast.warning("Selecciona al menos un destinatario");
      return;
    }
    setSending(true);
    try {
      const url = endpoint.replace("{id}", item.id);
      const { data } = await api.post(url, {
        destinatarios: Array.from(selected),
        mensaje: mensaje.trim() || null,
      });
      toast.success(`${tipo === "tarea" ? "Tarea" : "Evento"} enviado a ${data.enviados} destinatario(s)`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-email-dialog">
        <DialogHeader>
          <DialogTitle>
            <Send size={16} className="inline mr-2 text-[#205c45]" />
            Compartir {tipo} por correo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#e9f2ee] border-l-3 border-[#205c45] rounded p-2.5 text-sm">
            <div className="text-[10px] uppercase text-[#5c7368] mb-0.5">{tipo === "tarea" ? "Tarea" : "Evento"}</div>
            <div className="font-semibold text-[#15241d]">{item.titulo}</div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Destinatarios (usuarios del sistema)</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-[#8ba397]" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="pl-8 h-9 text-sm"
                data-testid="share-filter-input"
              />
            </div>
            <div className="border border-[#d1dbd6] rounded max-h-44 overflow-y-auto bg-white">
              {filtered.length === 0 && (
                <div className="text-center py-4 text-xs text-[#8ba397]">Sin usuarios</div>
              )}
              {filtered.map(u => (
                <label
                  key={u.id}
                  htmlFor={`share-u-${u.id}`}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-[#f7f9f8] cursor-pointer border-b border-[#e6ecea] last:border-0"
                  data-testid={`share-user-${u.id}`}
                >
                  <Checkbox
                    id={`share-u-${u.id}`}
                    checked={selected.has(u.id)}
                    onCheckedChange={() => toggle(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#15241d] truncate">{u.nombres} {u.apellidos}</div>
                    <div className="text-[11px] text-[#8ba397] truncate">{u.email}</div>
                  </div>
                </label>
              ))}
            </div>
            {selected.size > 0 && (
              <div className="text-[11px] text-[#205c45]">
                {selected.size} destinatario(s) seleccionado(s)
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Mensaje (opcional)</Label>
            <Textarea
              rows={3}
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              placeholder="Mensaje breve que acompaña el PDF (opcional)..."
              data-testid="share-mensaje-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancelar</Button>
          <Button
            onClick={send}
            className="bg-[#205c45] hover:bg-[#164430]"
            disabled={sending || selected.size === 0}
            data-testid="share-send-btn"
          >
            {sending ? "Enviando..." : <><Send size={13} className="mr-1.5" />Enviar PDF</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
